import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import { StyledTabs, StyledTab } from "components/CustomTabs";
import DescriptionIcon from "@mui/icons-material/Description";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import Div from "@jumbo/shared/Div";
import ProblemForm from "./BasicDetails/ProblemForm";
import { useParams, useNavigate } from "react-router-dom";
import EvaluationAndSubmissionPlayground from "./PlaygroundTab/EvaluationAndSubmissionPlayground";
import FormActions from "components/FormActions";
import { BASE_PATHS, SUB_PATHS } from "app/utils/constants/routeConstants";
import problemServices from "app/services/problem-service";
import useMetadata from "app/hooks/useMetadata";
import useProblemDetailsByName from "app/hooks/useProblemDetailsByName";
import useProblemDetailsById from "app/hooks/useProblemDetailsById";
import { TabPanel } from "components/TabPanel";
import ContentHeader from "components/ContentHeader/ContentHeader";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import JumboCustomizerButton from "app/shared/JumboCustomizer/JumboCustomizerButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSnackbar } from "notistack";
import useMetaTitle from "app/hooks/useMetaTitle";
import LoadingView from "components/LoadingView";

// Utility to check if a value is empty
const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && (value.length === 0 || value.every(isEmptyValue))) ||
  (typeof value === "object" && Object.values(value).every(isEmptyValue));

// Check if problem data lacks required fields
const isProblemDataEmpty = (data) =>
  ["name", "defaultTitle", "description"].every((field) =>
    isEmptyValue(data[field])
  );

const ProblemTabs = () => {
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  const { id } = useParams();
  const isAdmin = useAdmin();
  const isUpdate = !!id;
  const { problemDetailsByName } = useProblemDetailsByName(id) || {};
  const { problemDetailsById, loading: fetchLoading } =
    useProblemDetailsById(id) || {};
  const { metadata } = useMetadata();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useMetaTitle({
    title: isUpdate ? problemDetailsById?.defaultTitle : "Add Problem",
  });

  const addRoute =
    isUpdate && `${BASE_PATHS.ADMIN_PROBLEMS}/${SUB_PATHS.UPDATE_PROBLEM}`;

  const fetchedProblemData = useMemo(
    () => (id ? problemDetailsById : problemDetailsByName),
    [id, problemDetailsById, problemDetailsByName]
  );

  const defaultProblemData = useMemo(
    () => ({
      name: null,
      defaultTitle: null,
      description: null,
      status: "ACTIVE",
      forPaidOnly: false,
      problemReactions: [],
      problemDifficulties: [],
      problemTags: [],
      problemCompanies: [],
      problemTranslations: [],
      problemVideos: [],
      problemTests: [],
      problemSubmissionTemplates: [],
      problemEvaluationTemplates: [],
    }),
    []
  );

  const [problemData, setProblemData] = useState(() => {
    const savedData = !isUpdate && localStorage.getItem("problemFormData");
    return savedData ? JSON.parse(savedData) : defaultProblemData;
  });

  useEffect(() => {
    return () => {
      if (!isUpdate)
        localStorage.setItem("problemFormData", JSON.stringify(problemData));
    };
  }, [problemData, isUpdate]);

  useEffect(() => {
    if (isUpdate && fetchedProblemData) {
      setProblemData((prev) => {
        const newData = {
          ...defaultProblemData,
          ...fetchedProblemData,
          id: fetchedProblemData.id || id,
          problemReactions: fetchedProblemData.problemReactions?.length
            ? fetchedProblemData.problemReactions
            : prev.problemReactions,
          problemDifficulties: fetchedProblemData.problemDifficulties?.length
            ? fetchedProblemData.problemDifficulties
            : prev.problemDifficulties,
          problemTags: fetchedProblemData.problemTags?.length
            ? fetchedProblemData.problemTags.map((tag) => ({
                id: tag.id || Date.now(),
                name: tag.name,
              }))
            : prev.problemTags,
          problemCompanies: fetchedProblemData.problemCompanies?.length
            ? fetchedProblemData.problemCompanies
            : prev.problemCompanies,
          problemTranslations: fetchedProblemData.problemTranslations?.length
            ? fetchedProblemData.problemTranslations
            : prev.problemTranslations,
          problemVideos: fetchedProblemData.problemVideos?.length
            ? fetchedProblemData.problemVideos
            : prev.problemVideos,
          problemTests: fetchedProblemData.problemTests?.length
            ? fetchedProblemData.problemTests
            : prev.problemTests,
          problemSubmissionTemplates: fetchedProblemData
            .problemSubmissionTemplates?.length
            ? fetchedProblemData.problemSubmissionTemplates
            : prev.problemSubmissionTemplates,
          problemEvaluationTemplates: fetchedProblemData
            .problemEvaluationTemplates?.length
            ? fetchedProblemData.problemEvaluationTemplates
            : prev.problemEvaluationTemplates,
        };
        setNavigationLoading(false);
        return newData;
      });
    }
  }, [fetchedProblemData, id, isUpdate, defaultProblemData]);

  const cleanProblemData = useCallback(
    (data) => {
      const validVariableTypes = metadata?.variableTypes?.map(
        (type) => type.key
      ) || ["INTEGER"];
      const cleanedTests = data.problemTests
        .filter(
          (test) =>
            test.problemInputs.some(
              (i) => i.variableName !== null && i.value !== null
            ) &&
            test.problemOutputs.some(
              (o) => o.variableName !== null && o.value !== null
            )
        )
        .map((test) => ({
          ...test,
          problemInputs: test.problemInputs.filter(
            (i) =>
              i.variableName !== null &&
              i.value !== null &&
              validVariableTypes.includes(i.variableType)
          ),
          problemOutputs: test.problemOutputs.filter(
            (o) =>
              o.variableName !== null &&
              o.value !== null &&
              validVariableTypes.includes(o.variableType)
          ),
        }));

      const filterTemplates = (templates) =>
        templates.filter(
          (t) =>
            t.programmingLanguage &&
            (t.template !== null || t.description !== null)
        );

      const cleanedData = {
        ...data,
        problemTests: cleanedTests.length > 0 ? cleanedTests : null,
        problemSubmissionTemplates: filterTemplates(
          data.problemSubmissionTemplates
        ),
        problemEvaluationTemplates: filterTemplates(
          data.problemEvaluationTemplates
        ),
        problemVideos:
          data.problemVideos?.length > 0 ? data.problemVideos : null,
        problemCompanies:
          data.problemCompanies?.length > 0 ? data.problemCompanies : null,
        problemDifficulties:
          data.problemDifficulties?.length > 0
            ? data.problemDifficulties
            : null,
        problemReactions:
          data.problemReactions?.length > 0 ? data.problemReactions : null,
        problemTranslations:
          data.problemTranslations?.length > 0
            ? data.problemTranslations
            : null,
      };

      return cleanedData;
    },
    [metadata]
  );

  const handleSaveProblem = useCallback(async () => {
    setLoading(true);
    try {
      const cleanedData = cleanProblemData(problemData);
      if (!cleanedData.name) throw new Error("Problem name is required");
      if (
        !isUpdate &&
        cleanedData.problemTests?.length > 0 &&
        !cleanedData.problemTests.every(
          (test) =>
            test.problemInputs.length > 0 && test.problemOutputs.length > 0
        )
      ) {
        throw new Error(
          "Each test must have at least one input and one output"
        );
      }
      if (isUpdate && cleanedData.id) {
        if (
          cleanedData.problemTests?.length > 0 &&
          !cleanedData.problemTests.every(
            (test) =>
              test.problemInputs.length > 0 && test.problemOutputs.length > 0
          )
        ) {
          throw new Error(
            "Each test must have at least one input and one output"
          );
        }
        await problemServices.updateProblem(cleanedData);
        enqueueSnackbar(`Problem ${cleanedData.name} updated successfully.`, {
          variant: "success",
        });
      } else {
        await problemServices.createProblem(
          isProblemDataEmpty(cleanedData) ? null : cleanedData
        );
        enqueueSnackbar(`Problem ${cleanedData.name} created successfully.`, {
          variant: "success",
        });
      }
      setProblemData(defaultProblemData);
      localStorage.removeItem("problemFormData");
      navigate(`${BASE_PATHS.ALGOS_AND_DS}/${problemData.name}`);
    } catch (error) {
      enqueueSnackbar(`Failed to save problem: ${error.message}`, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    problemData,
    isUpdate,
    cleanProblemData,
    navigate,
    defaultProblemData,
    enqueueSnackbar,
  ]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAction = useCallback(
    (action) => {
      switch (action.id) {
        case "back":
          handleBack();
          break;
        default:
          console.log("Unknown action:", action);
      }
    },
    [handleBack]
  );

  const handlePreviousProblem = useCallback(() => {
    setNavigationLoading(true);
    navigate(`${addRoute}/${problemData.previous?.id}`);
  }, [navigate, problemData, addRoute]);

  const handleNextProblem = useCallback(() => {
    setNavigationLoading(true);
    navigate(`${addRoute}/${problemData.next?.id}`);
  }, [navigate, problemData, addRoute]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        gap: 2,
      }}
    >
      {navigationLoading || fetchLoading ? (
        <LoadingView message="Loading..." />
      ) : (
        <JumboCardQuick
          title={
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <ContentHeader
                isAdmin={isAdmin}
                onAction={handleAction}
                allowedActions={["back"]}
                showLeftTabs={true}
              />
              <FormActions
                onCancel={() => {
                  localStorage.removeItem("problemFormData");
                  setProblemData(defaultProblemData);
                  navigate(-1);
                }}
                onSave={handleSaveProblem}
                saveText="Save"
                isUpdate={isUpdate}
                loading={loading}
              />
            </Box>
          }
          headerSx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiCardHeader-action": { my: -0.75 },
          }}
          wrapperSx={{ p: 0 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            "& .MuiCardContent-root:last-child": {
              paddingBottom: 0,
            },
          }}
        >
          <Div
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
            }}
          >
            <StyledTabs
              value={value}
              onChange={(_, newValue) => setValue(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flexGrow: 1 }}
            >
              <StyledTab
                icon={<DescriptionIcon />}
                iconPosition="start"
                label="Basic Details"
              />
              <StyledTab
                icon={<ImportContactsIcon />}
                iconPosition="start"
                label="Playground"
              />
            </StyledTabs>
          </Div>
          <TabPanel value={value} index={0}>
            <ProblemForm
              problemData={problemData}
              setProblemData={setProblemData}
              isUpdate={isUpdate}
            />
          </TabPanel>
          <TabPanel value={value} index={1} sx={{ p: 0 }}>
            <EvaluationAndSubmissionPlayground
              problemData={problemData}
              setProblemData={setProblemData}
              isUpdate={isUpdate}
            />
          </TabPanel>
        </JumboCardQuick>
      )}
      {isUpdate && problemData.previous !== null && (
        <JumboCustomizerButton
          icon={<ArrowBackIcon />}
          onClick={handlePreviousProblem}
          position="left"
          ariaLabel="Previous Problem"
          sx={{
            bottom: 100,
          }}
        />
      )}
      {isUpdate && problemData.next !== null && (
        <JumboCustomizerButton
          icon={<ArrowForwardIcon />}
          onClick={handleNextProblem}
          position="right"
          ariaLabel="Next Problem"
          sx={{
            bottom: 100,
          }}
        />
      )}
      <FormActions
        onCancel={() => {
          localStorage.removeItem("problemFormData");
          setProblemData(defaultProblemData);
          navigate(-1);
        }}
        onSave={handleSaveProblem}
        saveText="Save"
        isUpdate={isUpdate}
        loading={loading}
      />
    </Box>
  );
};

export default ProblemTabs;
